import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { productPath, productsData, searchProducts } from "../data/productsData";
import "./styles/SearchOverlay.css";

const SUGGESTIONS = ["potassium", "molybdate", "humic", "fertilizer", "glass", "feed"];

/**
 * Command-palette style catalogue search. Opened from the nav or with ⌘K.
 * Arrow keys move the selection, Enter opens the highlighted product, or
 * falls through to the full /search results page when nothing is highlighted.
 */
const SearchOverlay = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const results = useMemo(() => {
    if (!query.trim()) return productsData.slice(0, 6);
    return searchProducts(query).slice(0, 8);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCursor(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    document.body.classList.add("no-scroll");
    return () => {
      cancelAnimationFrame(id);
      document.body.classList.remove("no-scroll");
    };
  }, [open]);

  useEffect(() => setCursor(0), [query]);

  if (!open) return null;

  const go = (product) => {
    onClose();
    navigate(productPath(product));
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (results[cursor]) go(results[cursor]);
      else if (query.trim()) {
        onClose();
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }
    }
  };

  return createPortal(
    <div className="so" role="dialog" aria-modal="true" aria-label="Search the catalogue">
      <button
        type="button"
        className="so__scrim"
        onClick={onClose}
        aria-label="Close search"
        tabIndex={-1}
      />

      <div className="so__panel">
        <div className="so__field">
          <i className="fas fa-search so__field-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search compounds, formulas or applications…"
            aria-label="Search query"
            autoComplete="off"
            spellCheck="false"
          />
          <button type="button" className="so__esc" onClick={onClose}>
            Esc
          </button>
        </div>

        <div className="so__body">
          <p className="so__hint">
            {query.trim()
              ? `${results.length} match${results.length === 1 ? "" : "es"}`
              : "In the catalogue"}
          </p>

          {results.length === 0 ? (
            <div className="so__none">
              <p>
                Nothing matches “{query}”. We import to order too, so tell us
                what you need.
              </p>
              <button
                type="button"
                className="btn btn--accent btn--sm"
                onClick={() => {
                  onClose();
                  navigate("/contact");
                }}>
                Request a compound
                <span className="btn__arrow" aria-hidden="true">→</span>
              </button>
            </div>
          ) : (
            <ul className="so__list">
              {results.map((p, i) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`so__row${i === cursor ? " is-active" : ""}`}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => go(p)}>
                    <span className="so__thumb">
                      <img src={p.image} alt="" loading="lazy" />
                    </span>
                    <span className="so__text">
                      <span className="so__name">{p.name}</span>
                      <span className="so__sub">
                        {p.category} · {p.grade} · {p.purity}
                      </span>
                    </span>
                    <span className="so__formula mono">{p.formula}</span>
                    <span className="so__go" aria-hidden="true">↗</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!query.trim() && (
            <div className="so__suggest">
              <span className="so__hint">Try</span>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="so__tag"
                  onClick={() => setQuery(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="so__foot mono">
          <span>↑ ↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchOverlay;
